import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import type { DocumentPackageItem, DocumentType } from "../api/types";
import { Icon } from "../components/Icon";
import { ProjectInput } from "../components/ProjectInput";
import { trackEvent } from "../telemetry";

export function PackageConfigurationPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const packageQuery = useQuery({
    queryKey: ["document-package", docId],
    queryFn: () => api.getDocumentPackage(docId!),
    enabled: Boolean(docId),
  });
  const [documents, setDocuments] = useState<DocumentPackageItem[]>([]);
  const [projectName, setProjectName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!packageQuery.data) return;
    setDocuments(packageQuery.data.documents);
    setProjectName(packageQuery.data.project_name ?? "");
  }, [packageQuery.data]);

  const update = (id: string, patch: Partial<DocumentPackageItem>) => {
    setDocuments((current) =>
      current.map((document) => (document.id === id ? { ...document, ...patch } : document)),
    );
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= documents.length) return;
    setDocuments((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((document, position) => ({ ...document, position }));
    });
  };

  const save = async () => {
    if (!docId) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateDocumentPackage(docId, {
        project_name: projectName.trim() || null,
        documents: documents.map((document, position) => ({
          id: document.id,
          document_type: document.document_type,
          document_date: document.document_date || null,
          document_number: document.document_number?.trim() || null,
          position,
        })),
      });
      trackEvent("package_configured", {
        document_id: docId,
        document_count: documents.length,
        agreement_count: documents.filter((document) => document.document_type === "agreement").length,
        annex_count: documents.filter((document) => document.document_type === "annex").length,
        dated_count: documents.filter((document) => Boolean(document.document_date)).length,
      });
      navigate(`/docs/${docId}/wizard`);
    } catch (exc) {
      setError(String(exc));
    } finally {
      setBusy(false);
    }
  };

  if (packageQuery.isLoading) return <div style={{ padding: 24 }}>Loading package…</div>;
  if (packageQuery.error) return <div className="package-error">{String(packageQuery.error)}</div>;

  return (
    <div className="package-config">
      <div className="page-head">
        <div>
          <h1 className="page-title">Configure agreement package</h1>
          <div className="page-sub">
            Review or correct metadata detected automatically after OCR.
          </div>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <ProjectInput value={projectName} onChange={setProjectName} />
      </div>

      <div className="card package-config-card">
        {documents.map((document, index) => (
          <div className="package-document" key={document.id}>
            <div className="package-order">
              <span>{index + 1}</span>
              <div>
                <button
                  className="icon-btn"
                  disabled={index === 0}
                  aria-label={`Move ${document.filename} up`}
                  onClick={() => move(index, -1)}
                >
                  <Icon name="chevUp" size={16} />
                </button>
                <button
                  className="icon-btn"
                  disabled={index === documents.length - 1}
                  aria-label={`Move ${document.filename} down`}
                  onClick={() => move(index, 1)}
                >
                  <Icon name="chevDown" size={16} />
                </button>
              </div>
            </div>
            <div className="package-document-body">
              <div className="package-filename">
                {document.filename}
                {document.is_root && <span className="badge badge-neutral">Package ID</span>}
              </div>
              <div className="package-fields">
                <label>
                  <span>Document type</span>
                  <select
                    value={document.document_type}
                    onChange={(event) =>
                      update(document.id, { document_type: event.target.value as DocumentType })
                    }
                  >
                    <option value="agreement">Agreement</option>
                    <option value="annex">Annex</option>
                  </select>
                </label>
                <label>
                  <span>Date</span>
                  <input
                    type="date"
                    value={document.document_date ?? ""}
                    onChange={(event) => update(document.id, { document_date: event.target.value })}
                  />
                </label>
                <label>
                  <span>Number</span>
                  <input
                    type="text"
                    maxLength={200}
                    placeholder="e.g. U/12/2024"
                    value={document.document_number ?? ""}
                    onChange={(event) =>
                      update(document.id, { document_number: event.target.value })
                    }
                  />
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && <div className="package-error">{error}</div>}
      <div className="package-actions">
        <button className="btn btn-secondary" onClick={() => navigate(`/docs/${docId}/wizard`)} disabled={busy}>
          Back to analysis
        </button>
        <button className="btn btn-primary btn-lg" onClick={save} disabled={busy || !documents.length}>
          {busy ? "Saving…" : "Save corrections"}
          <Icon name="arrowRight" size={14} />
        </button>
      </div>
    </div>
  );
}
